import React, { useEffect, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import Modal from 'react-bootstrap/Modal';
import mapValues from 'lodash/mapValues';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { X, Plus, Minus } from 'lucide-react';
import {
  getPresetItemQuantity,
  getPresetItemTotalPrice,
  extractRecipientName,
  buildServingDetailsFromRecipientName,
} from 'utils/preset.util';
import { INVALID_INTEGER } from 'consts/form';
import { priceDecimalFormatter } from 'formatter/preset.formatter';
import FormError from '../FormError';
import './index.scss';
import OrderError from '../OrderError';

const settings = {
  autoplay: true,
  autoplaySpeed: 2000,
  arrows: true,
  dots: false,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const validationSchema = yup.object().shape({
  specialInstruction: yup.string(),
  quantity: yup
    .number()
    .integer(INVALID_INTEGER)
    .min(0, INVALID_INTEGER),
  menuItemsMapping: yup.lazy(obj =>
    yup.object(
      mapValues(obj, () => {
        return yup
          .number()
          .integer(INVALID_INTEGER)
          .min(0, INVALID_INTEGER);
      }),
    ),
  ),
});

const onQuantityInput = (e, setFieldValue) => {
  const { currentTarget } = e;
  // now need to save only integers
  const newValue =
    parseInt(currentTarget.value.replace(/[^0-9]/g, ''), 10) || 0;
  setFieldValue(currentTarget.name, newValue);
};

const onQuantityChange = (name, currentValue, setFieldValue, added = 1) => {
  const newValue = Math.max(parseInt(currentValue, 10) + added, 0);
  setFieldValue(name, newValue);
};

const QuantityStepper = ({ name, value, onDecrease, onIncrease, onInput }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={onDecrease}
      disabled={(value || 0) <= 0}
      className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-700 hover:border-[#E53935] hover:text-[#E53935] disabled:opacity-30 disabled:pointer-events-none transition-colors"
      aria-label="Decrease quantity"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>
    <Field
      type="number"
      className="counter__number font-bold text-base text-[#202124] min-w-[28px] text-center bg-transparent focus:outline-none"
      name={name}
      value={value || 0}
      onChange={onInput}
    />
    <button
      type="button"
      onClick={onIncrease}
      className="w-8 h-8 rounded-full border border-[#E53935] bg-[#E53935] text-white flex items-center justify-center hover:bg-[#d32f2f] transition-colors"
      aria-label="Increase quantity"
    >
      <Plus className="w-3.5 h-3.5" />
    </button>
  </div>
);

const PresetItemView = ({
  presetItem,
  initialValues,
  chefInfo,
  onClose,
  onSubmit,
}) => {
  const [minOrderSelectionValid, setMinOrderSelectionValid] = useState(true);
  const initialRecipientName = extractRecipientName(
    initialValues?.servingDetails,
  );
  const [recipientName, setRecipientName] = useState(initialRecipientName);
  const [showRecipientInput, setShowRecipientInput] = useState(
    !!initialRecipientName,
  );

  // presetItem/initialValues change identity when the customer opens a
  // different item without this component unmounting - keep the recipient
  // name in sync with whichever item is currently open.
  useEffect(() => {
    setRecipientName(initialRecipientName);
    setShowRecipientInput(!!initialRecipientName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetItem?.id]);

  if (!presetItem) return null;
  const {
    name,
    description,
    averageRating,
    images,
    menuItems,
    priceCents,
    dietaryTypes,
  } = presetItem;
  const parsedDescription =
    description && description.replace(/(\r\n|\n|\r)/gm, '<br />');
  return (
    <Modal
      backdrop="static"
      show
      centered
      size="lg"
      onHide={onClose}
      contentClassName="!rounded-2xl md:!rounded-3xl !border !border-panda-tint !overflow-hidden !p-0 !shadow-2xl relative"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close modal"
        className="absolute right-3 top-3 z-20 w-9 h-9 rounded-full bg-[#202124] text-white hover:bg-[#E53935] flex items-center justify-center shadow-md transition-all active:scale-95"
      >
        <X className="w-5 h-5" />
      </button>
      <Modal.Body className="!p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 max-h-[85vh] overflow-y-auto">
          <div className="p-5 sm:p-6 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col justify-between bg-white">
            <div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#202124] leading-snug">
                    {name}
                  </h2>
                  <div className="star-ratings-sprite mt-1" style={{ width: '80px' }}>
                    <span
                      style={{ width: `${averageRating * 20}%` }}
                      className="star-ratings-sprite-rating"
                    />
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-gray-200 bg-panda-tint flex items-center justify-center">
                    <img
                      className="restaurant w-full h-full object-cover"
                      src={chefInfo.chefImage}
                      alt="chefImage"
                      referrerPolicy="no-referrer"
                      onError={e => {
                        e.target.onerror = null;
                        e.target.src = '/panda_avatar.jpg';
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-[#E53935] font-heading font-bold text-sm mb-4">
                <span>
                  ${priceDecimalFormatter(getPresetItemTotalPrice(presetItem))}
                </span>
                <span className="text-xs text-gray-500 font-normal">
                  {' '}
                  / {getPresetItemQuantity(presetItem)} quantity
                </span>
              </div>
              <div className="relative rounded-2xl overflow-hidden mb-4 shadow-sm bg-gray-100">
                <Slider className="slick preview" {...settings}>
                  {images &&
                    images.map(image => (
                      <div key={image} className="slide">
                        <img
                          src={image}
                          alt="presetItemImage"
                          referrerPolicy="no-referrer"
                          onError={e => {
                            e.target.onerror = null;
                            e.target.src = '/meal_default.png';
                          }}
                        />
                      </div>
                    ))}
                </Slider>
              </div>
              {dietaryTypes && (
                <ul className="tags flex flex-wrap gap-1.5 mb-3">
                  {dietaryTypes
                    .split(',')
                    .map(
                      dietaryType =>
                        dietaryType && (
                          <li
                            key={dietaryType}
                            className="px-2.5 py-0.5 rounded-full bg-panda-tint text-[#E53935] text-[11px] font-semibold"
                          >
                            {dietaryType}
                          </li>
                        ),
                    )}
                </ul>
              )}
              <div className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                {ReactHtmlParser(parsedDescription)}
              </div>
            </div>
          </div>
          <Formik
            {...{ initialValues, validationSchema }}
            validateOnChange
            validateOnBlur
            onSubmit={form => {
              const eachOrderValidation =
                form?.menuItems?.length > 0
                  ? Object.values(form?.menuItemsMapping).every(
                      value => value === 0,
                    ) ||
                    Object.values(form?.menuItemsMapping).some(
                      value => value > 0 && value < chefInfo.minOrderAmount,
                    )
                  : form.quantity < chefInfo.minOrderAmount;

              if (eachOrderValidation) {
                setMinOrderSelectionValid(false);
                return;
              }
              const quantity = getPresetItemQuantity(form);
              const priceCents = getPresetItemTotalPrice(form) / quantity;
              const quantitiesById =
                menuItems?.length > 0
                  ? form.menuItemsMapping
                  : { [presetItem.menuId]: form.quantity };
              onSubmit(presetItem, {
                ...form,
                quantity,
                priceCents,
                servingDetails: buildServingDetailsFromRecipientName(
                  recipientName,
                  quantitiesById,
                ),
              });
              setMinOrderSelectionValid(true);
              onClose();
            }}
          >
            {({ setFieldValue, values }) => {
              const totalQuantity = getPresetItemQuantity(values);
              const totalPrice = getPresetItemTotalPrice(values);
              return (
                <Form className="p-5 sm:p-6 bg-panda-tint/40 flex flex-col justify-between">
                  <div className="space-y-5">
                    <div>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Order Selection (Minimum of {chefInfo.minOrderAmount})
                      </p>
                      <div className="space-y-2">
                        {menuItems.map(menuItem => {
                          const { id, name, priceCents } = menuItem;
                          return (
                            <div
                              key={id}
                              className="bg-white rounded-xl p-3.5 border border-gray-200 shadow-xs flex items-center justify-between"
                            >
                              <div>
                                <span className="font-heading font-bold text-sm text-[#202124] block">
                                  {name}
                                </span>
                                <span className="text-xs font-semibold text-[#E53935]">
                                  ${priceDecimalFormatter(priceCents)}
                                </span>
                              </div>
                              <QuantityStepper
                                name={`menuItemsMapping.${menuItem.id}`}
                                value={values.menuItemsMapping?.[menuItem.id]}
                                onInput={e => onQuantityInput(e, setFieldValue)}
                                onDecrease={() =>
                                  onQuantityChange(
                                    `menuItemsMapping.${menuItem.id}`,
                                    values.menuItemsMapping[menuItem.id] || 0,
                                    setFieldValue,
                                    -1,
                                  )
                                }
                                onIncrease={() =>
                                  onQuantityChange(
                                    `menuItemsMapping.${menuItem.id}`,
                                    values.menuItemsMapping[menuItem.id] || 0,
                                    setFieldValue,
                                  )
                                }
                              />
                            </div>
                          );
                        })}
                        {menuItems.length === 0 && (
                          <div className="bg-white rounded-xl p-3.5 border border-gray-200 shadow-xs">
                            <div className="flex items-center justify-between gap-2">
                              <div>
                                <span className="font-heading font-bold text-sm text-[#202124] block">
                                  {name}
                                </span>
                                <span className="text-xs font-semibold text-[#E53935]">
                                  ${priceDecimalFormatter(priceCents)}
                                </span>
                              </div>
                              <QuantityStepper
                                name="quantity"
                                value={values.quantity}
                                onInput={e => onQuantityInput(e, setFieldValue)}
                                onDecrease={() =>
                                  onQuantityChange(
                                    'quantity',
                                    values.quantity || 0,
                                    setFieldValue,
                                    -1,
                                  )
                                }
                                onIncrease={() =>
                                  onQuantityChange(
                                    'quantity',
                                    values.quantity || 0,
                                    setFieldValue,
                                  )
                                }
                              />
                            </div>
                            <FormError name="quantity" />
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-right">
                        {!showRecipientInput ? (
                          <button
                            type="button"
                            onClick={() => setShowRecipientInput(true)}
                            className="text-xs font-semibold text-[#E53935] hover:underline inline-flex items-center gap-1"
                          >
                            + Add Name for Meal Label
                          </button>
                        ) : (
                          <div className="mt-1 text-left">
                            <label className="text-[11px] text-gray-500 font-medium">
                              Meal Label (e.g. Kim&apos;s Lunch)
                            </label>
                            <input
                              type="text"
                              placeholder="Who is this portion for?"
                              value={recipientName}
                              onChange={e => setRecipientName(e.target.value)}
                              className="w-full mt-0.5 px-3 py-1.5 text-xs bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#E53935]"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
                        Special Instructions
                      </label>
                      <Field
                        as="textarea"
                        className="w-full p-3 bg-white rounded-xl border border-gray-200 text-xs text-[#202124] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E53935] focus:border-transparent resize-none shadow-xs"
                        name="specialInstruction"
                        rows={3}
                        placeholder="Please type in any special instructions you have."
                      />
                    </div>
                    {!minOrderSelectionValid && (
                      <OrderError
                        errorText={`Minimum order amount of ${chefInfo.minOrderAmount} per item not met. Please increase quantity and continue.`}
                      />
                    )}
                    <div className="pt-2 border-t border-gray-200 space-y-1">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Total Portions:</span>
                        <span className="font-bold text-[#202124]">{totalQuantity}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold text-[#202124]">
                        <span>Total Price:</span>
                        <span className="text-[#E53935] text-base">
                          ${priceDecimalFormatter(totalPrice)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5">
                    <button
                      className="w-full py-3.5 px-6 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] text-white font-heading font-bold text-base shadow-md shadow-red-500/20 active:scale-98 transition-all flex items-center justify-center gap-2"
                      type="submit"
                    >
                      Save Changes
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

PresetItemView.propTypes = {
  presetItem: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    quantity: PropTypes.number,
    priceCents: PropTypes.number,
    servingStyle: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    averageRating: PropTypes.number,
    menuItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        priceCents: PropTypes.number,
      }),
    ),
    dietaryTypes: PropTypes.string,
  }),
  initialValues: PropTypes.object,
  chefInfo: PropTypes.shape({
    chefImage: PropTypes.string,
    menuType: PropTypes.string,
    minOrderAmount: PropTypes.number,
  }),
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

PresetItemView.defaultProps = {
  presetItem: null,
  initialValues: {},
  chefInfo: {},
  onClose: () => {},
  onSubmit: () => {},
};
export default PresetItemView;
